import {
  getEnvironmentValue,
  GA_SERVICE,
  GA_SERVICE_V2,
  ENABLE_API_VERSIONING,
} from '../envConfig';
import { ENDPOINT_GROUPS } from './endpointConfig';

export const API_VERSIONS = {
  DEFAULT: 'default',
  V1: 'v1',
  V2: 'v2',
};

const OVERRIDES_KEY = 'api_version_overrides_map';

export function getEndpointOverrides() {
  try {
    const stored = localStorage.getItem(OVERRIDES_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading API overrides:', error);
    return {};
  }
}

export function setEndpointOverride(id, version) {
  try {
    const overrides = getEndpointOverrides();
    if (version === API_VERSIONS.DEFAULT) {
      delete overrides[id];
    } else {
      overrides[id] = version;
    }
    localStorage.setItem(OVERRIDES_KEY, JSON.stringify(overrides));
  } catch (error) {
    console.error('Error saving API override:', error);
  }
}

export function getBaseUrl(version = API_VERSIONS.DEFAULT) {
  const v1BaseUrl = getEnvironmentValue(GA_SERVICE) || '';
  const v2BaseUrl = getEnvironmentValue(GA_SERVICE_V2) || '';

  const urlMap = {
    [API_VERSIONS.V2]: v2BaseUrl,
    [API_VERSIONS.V1]: v1BaseUrl,
    [API_VERSIONS.DEFAULT]: v1BaseUrl,
  };

  return urlMap[version] || v1BaseUrl;
}

function matchesUrlPattern(url, pattern) {
  if (typeof pattern === 'string') {
    return url === pattern;
  }

  if (pattern.base && pattern.dynamic) {
    const basePath = pattern.base;
    if (url.startsWith(basePath + '/')) {
      const suffix = url.slice(basePath.length + 1);
      return !suffix.includes('/');
    }
    if (url.startsWith(basePath + '?')) {
      return true;
    }
    return url === basePath;
  }

  return false;
}

function findMatchingEndpoint(url, endpointGroups) {
  // Pass 1: exact string matches take priority over dynamic patterns
  for (const group of endpointGroups) {
    for (const endpoint of group.endpoints) {
      for (const pattern of endpoint.urlPatterns) {
        if (typeof pattern === 'string' && url === pattern) {
          return { group, endpoint, pattern };
        }
      }
    }
  }

  // Pass 2: dynamic patterns (e.g. { base, dynamic: true })
  for (const group of endpointGroups) {
    for (const endpoint of group.endpoints) {
      for (const pattern of endpoint.urlPatterns) {
        if (typeof pattern !== 'string' && matchesUrlPattern(url, pattern)) {
          return { group, endpoint, pattern };
        }
      }
    }
  }

  return null;
}

function buildTransformedUrl(url, effectiveVersion) {
  if (effectiveVersion !== API_VERSIONS.V2) {
    return url;
  }

  const v1BaseUrl = getBaseUrl(API_VERSIONS.V1);
  const v2BaseUrl = getBaseUrl(API_VERSIONS.V2);
  if (url.startsWith(v1BaseUrl)) {
    return url.replace(v1BaseUrl, v2BaseUrl);
  }

  return url;
}

/**
 * Calculate the effective API version for an endpoint
 * Priority: localStorage override → endpoint.version → 'v1' default
 */
export function getEffectiveVersion(endpoint, overrides) {
  // Priority 1: localStorage override
  if (overrides[endpoint.id]) {
    return overrides[endpoint.id];
  }
  // Priority 2: Config version (defaults to v1 if not specified)
  return endpoint.version || API_VERSIONS.V1;
}

export function isApiVersioningEnabled() {
  return getEnvironmentValue(ENABLE_API_VERSIONING) === true;
}

export function describeApiRequest(url) {
  if (!url) {
    return {
      effectiveVersion: API_VERSIONS.DEFAULT,
      transformedUrl: url,
    };
  }

  const match = findMatchingEndpoint(url, ENDPOINT_GROUPS);
  if (!match) {
    return {
      effectiveVersion: API_VERSIONS.DEFAULT,
      transformedUrl: url,
      path: url,
    };
  }

  const overrides = getEndpointOverrides();
  const effectiveVersion = getEffectiveVersion(match.endpoint, overrides);
  const transformedUrl = buildTransformedUrl(url, effectiveVersion);
  let path = transformedUrl;

  try {
    path = new URL(transformedUrl).pathname;
  } catch (error) {
    path = transformedUrl;
  }

  return {
    endpointId: match.endpoint.id,
    endpointGroup: match.group.id,
    endpointGroupTitle: match.group.title,
    effectiveVersion,
    transformedUrl,
    path,
  };
}

export function transformUrl(url) {
  if (!url) return url;

  if (!isApiVersioningEnabled()) {
    return url;
  }

  return describeApiRequest(url).transformedUrl;
}
